import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Input,
  Modal,
  ModalBody,
  Nav,
  NavItem,
  NavLink,
  Spinner,
  TabContent,
  TabPane,
} from "reactstrap";
import { AUTOPILOT_COLORS } from "../../../components/autopilot/styles";
import api from "../../../services/api";
import { useAuth } from "../../../hooks/auth";
import IntegracaoTab from "./IntegracaoTab";
import VinculoConectorTab from "./VinculoConectorTab";
import ContatosTab from "./ContatosTab";
import CadastroEstabelecimentoForm, { resumoReceita } from "../../../components/CadastroEstabelecimentoForm";
import TransportadoraMonogram from "../../Empresa/Transportadoras/TransportadoraMonogram";
import { adminGetCadastro, adminAtualizarCadastro, adminAtualizarPelaReceita, adminHistoricoCadastro, mensagemErro } from "../../../services/meuFreteApi";
import { adminEnviarLogo, adminRemoverLogo, mensagemErroLogo } from "../../../services/transportadoraLogoApi";
import { useToast } from "../../../hooks/toast";
import { formatDateTime } from "../../../utils/formatDate";

const formatDoc = (v) => {
  const d = String(v || "").replace(/\D/g, "");
  if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
  return v || "-";
};

const formatCep = (v) => {
  const d = String(v || "").replace(/\D/g, "");
  return d.length === 8 ? d.replace(/(\d{5})(\d{3})/, "$1-$2") : v || "-";
};

const Campo = ({ label, value }) => (
  <div style={{ marginBottom: "0.75rem" }}>
    <div style={{ fontSize: "0.72rem", color: AUTOPILOT_COLORS.muted, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "2px" }}>
      {label}
    </div>
    <div style={{ fontSize: "0.88rem", color: AUTOPILOT_COLORS.text }}>{value || "-"}</div>
  </div>
);

const sectionTitle = (text) => (
  <div style={{ fontSize: "0.72rem", color: AUTOPILOT_COLORS.muted, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600, marginBottom: "0.75rem", marginTop: "0.25rem", borderBottom: `1px solid ${AUTOPILOT_COLORS.border}`, paddingBottom: "4px" }}>
    {text}
  </div>
);

// ─── Aba: Dados Gerais ────────────────────────────────────────────────────────
const TabDados = ({ dados }) => {
  if (!dados) return <div style={{ color: AUTOPILOT_COLORS.muted, padding: "1rem 0" }}>Carregando...</div>;
  return (
    <div>
      {sectionTitle("Identificação")}
      {/* Logo (PO1-2722), só leitura aqui: quem não é admin não edita o cadastro mestre. */}
      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.75rem" }}>
        <TransportadoraMonogram nome={dados.fantasia || dados.razaoSocial} size={56} logoUrl={dados.logoUrl} />
        <span style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.78rem" }}>{dados.logoUrl ? "Logo cadastrado" : "Sem logo — aparece o monograma com as iniciais"}</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 1.5rem" }}>
        <Campo label="CNPJ/CPF" value={formatDoc(dados.cnpjCpf)} />
        <Campo label="Status" value={dados.status ? "Ativo" : "Inativo"} />
        <Campo label="Razão Social" value={dados.razaoSocial} />
        <Campo label="Apelido" value={dados.fantasia} />
        <Campo label="Inscrição Estadual" value={dados.inscricaoEstadual} />
        <Campo label="Inscrição Municipal" value={dados.inscricaoMunicipal} />
        {dados.observacao && <Campo label="Observação" value={dados.observacao} />}
      </div>

      {sectionTitle("Endereço")}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 1.5rem" }}>
        <Campo label="Logradouro" value={dados.logradouro} />
        <Campo label="Número" value={dados.numero} />
        <Campo label="Complemento" value={dados.complemento} />
        <Campo label="Bairro" value={dados.bairro} />
        <Campo label="CEP" value={formatCep(dados.cep)} />
        <Campo label="Cidade / UF" value={dados.cidade ? `${dados.cidade} / ${dados.uf}` : null} />
      </div>
    </div>
  );
};

// ─── Aba: Dados editáveis pela Everlog (PO1-2308) ────────────────────────────
// A Everlog corrige sempre, com histórico (origem EVERLOG). Mesmo formulário do
// MeuFrete e do painel de habilitação.
const TabDadosEditavel = ({ transportadoraId, onChanged }) => {
  const { addToast } = useToast();
  const [estabelecimento, setEstabelecimento] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");

  const carregar = useCallback(async () => {
    try {
      setErro("");
      const r = await adminGetCadastro(transportadoraId);
      setEstabelecimento(r.estabelecimento);
    } catch (e) {
      setErro(mensagemErro(e, "Erro ao carregar o cadastro."));
    }
  }, [transportadoraId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const salvar = async (dados) => {
    setSalvando(true);
    try {
      const out = await adminAtualizarCadastro(transportadoraId, dados);
      addToast({ place: "tr", type: "success", title: "Cadastro atualizado", description: out.alterados && out.alterados.length ? `Campos: ${out.alterados.join(", ")}.` : "Nada mudou." });
      await carregar();
      if (onChanged) onChanged();
    } catch (e) {
      addToast({ place: "tr", type: "danger", title: "Não foi possível salvar", description: mensagemErro(e) });
    } finally {
      setSalvando(false);
    }
  };

  const receita = async () => {
    try {
      const out = await adminAtualizarPelaReceita(transportadoraId);
      addToast({ place: "tr", type: "success", title: "Receita consultada", description: resumoReceita(out) });
      await carregar();
      if (onChanged) onChanged();
    } catch (e) {
      addToast({ place: "tr", type: "danger", title: "Não foi possível consultar a Receita", description: mensagemErro(e) });
    }
  };

  // Logo pela Everlog (PO1-2722): global, vale para todos os embarcadores.
  const [logoBusy, setLogoBusy] = useState(false);
  const enviarLogo = async (blob, nome) => {
    setLogoBusy(true);
    try {
      await adminEnviarLogo(transportadoraId, blob, nome);
      addToast({ place: "tr", type: "success", title: "Logo atualizado", description: "Vale para todos os embarcadores que veem esta transportadora." });
      await carregar();
      if (onChanged) onChanged();
    } catch (e) {
      addToast({ place: "tr", type: "danger", title: "Não foi possível enviar o logo", description: mensagemErroLogo(e) });
    } finally {
      setLogoBusy(false);
    }
  };
  const removerLogo = async () => {
    setLogoBusy(true);
    try {
      await adminRemoverLogo(transportadoraId);
      addToast({ place: "tr", type: "success", title: "Logo removido" });
      await carregar();
      if (onChanged) onChanged();
    } catch (e) {
      addToast({ place: "tr", type: "danger", title: "Não foi possível remover o logo", description: mensagemErroLogo(e) });
    } finally {
      setLogoBusy(false);
    }
  };

  if (erro) return <div style={{ color: AUTOPILOT_COLORS.statusDanger, padding: "1rem 0" }}>{erro}</div>;
  if (!estabelecimento) return <div style={{ color: AUTOPILOT_COLORS.muted, padding: "1rem 0" }}>Carregando...</div>;
  return (
    <CadastroEstabelecimentoForm
      estabelecimento={estabelecimento}
      salvando={salvando}
      onSalvar={salvar}
      onReceita={receita}
      onEnviarLogo={enviarLogo}
      onRemoverLogo={removerLogo}
      logoBusy={logoBusy}
    />
  );
};

// ─── Aba: Histórico do cadastro (PO1-2308) ───────────────────────────────────
const CAMPO_LABEL = { fantasia: "Apelido", razaoSocial: "Razão social", logradouro: "Logradouro", numero: "Número", complemento: "Complemento", bairro: "Bairro", cep: "CEP", dddTelefone: "DDD", telefone: "Telefone" };
const ORIGEM_LABEL = { TRANSPORTADORA: "Transportadora", EVERLOG: "Everlog", RECEITA: "Receita Federal" };
const TabHistorico = ({ transportadoraId }) => {
  const [linhas, setLinhas] = useState(null);
  useEffect(() => {
    let vivo = true;
    adminHistoricoCadastro(transportadoraId)
      .then((r) => vivo && setLinhas(r.historico || []))
      .catch(() => vivo && setLinhas([]));
    return () => { vivo = false; };
  }, [transportadoraId]);
  if (!linhas) return <div style={{ color: AUTOPILOT_COLORS.muted, padding: "1rem 0" }}>Carregando...</div>;
  if (!linhas.length) return <div style={{ color: AUTOPILOT_COLORS.muted, padding: "1rem 0" }}>Nenhuma alteração registrada.</div>;
  return (
    <table className="table table-sm">
      <thead><tr><th>Quando</th><th>Campo</th><th>Antes</th><th>Depois</th><th>Origem</th><th>Quem</th></tr></thead>
      <tbody>
        {linhas.map((h) => (
          <tr key={h.id}>
            <td style={{ whiteSpace: "nowrap" }}>{formatDateTime(h.creationTime)}</td>
            <td>{CAMPO_LABEL[h.campo] || h.campo}</td>
            <td>{h.valorAnterior || "—"}</td>
            <td>{h.valorNovo || "—"}</td>
            <td>{ORIGEM_LABEL[h.origem] || h.origem}</td>
            <td>{h.userNome ? `${h.userNome} ${h.userSobrenome || ""}` : "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

// ─── Aba: Filiais (lista + associar/remover vínculo matriz/filial) ────────────
const erroBoxStyle = { background: AUTOPILOT_COLORS.statusDangerBg, border: `1px solid ${AUTOPILOT_COLORS.statusDangerBorder}`, borderRadius: "4px", padding: "0.55rem 0.85rem", color: AUTOPILOT_COLORS.statusDanger, fontSize: "0.8rem", marginBottom: "0.75rem" };
const hintStyle = { color: AUTOPILOT_COLORS.muted, fontSize: "0.8rem", padding: "0.5rem 0", textAlign: "center" };
const rowStyle = { background: AUTOPILOT_COLORS.insetSurface, borderRadius: "6px", padding: "0.55rem 0.9rem", display: "flex", alignItems: "center", justifyContent: "space-between" };

// PO1-3102 (mudança de escopo 3): associar e remover filial é só do admin.
const TabFiliais = ({ filiais, matrizId, embarcadorId, onChanged, podeEditar = true }) => {
  const [associando, setAssociando] = useState(false);
  const [busca, setBusca] = useState("");
  const [candidatos, setCandidatos] = useState([]);
  const [loadingCand, setLoadingCand] = useState(false);
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState("");

  const buscarCandidatos = useCallback(
    async (termo) => {
      if (!embarcadorId) return;
      try {
        setLoadingCand(true);
        setErro("");
        const res = await api.get(`/transportadoras/${matrizId}/candidatas-filial`, {
          params: { embarcadorId, search: termo || undefined },
        });
        setCandidatos(res.data || []);
      } catch (e) {
        setErro(e?.response?.data?.error || "Erro ao buscar transportadoras.");
      } finally {
        setLoadingCand(false);
      }
    },
    [matrizId, embarcadorId]
  );

  const abrirAssociar = () => {
    setErro("");
    setBusca("");
    setCandidatos([]);
    setAssociando(true);
    buscarCandidatos("");
  };

  const associar = async (idFilial) => {
    try {
      setSaving(true);
      setErro("");
      await api.post(`/transportadoras/${matrizId}/filiais`, { idFilial, embarcadorId });
      setAssociando(false);
      if (onChanged) onChanged();
    } catch (e) {
      setErro(e?.response?.data?.error || "Erro ao associar filial.");
    } finally {
      setSaving(false);
    }
  };

  const remover = async (idFilial) => {
    try {
      setSaving(true);
      setErro("");
      await api.delete(`/transportadoras/${matrizId}/filiais/${idFilial}`, {
        params: { embarcadorId },
      });
      if (onChanged) onChanged();
    } catch (e) {
      setErro(e?.response?.data?.error || "Erro ao remover filial.");
    } finally {
      setSaving(false);
    }
  };

  if (!filiais) return <div style={{ color: AUTOPILOT_COLORS.muted, padding: "1rem 0" }}>Carregando...</div>;

  return (
    <div>
      {erro && <div style={erroBoxStyle}>{erro}</div>}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
        <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.8rem" }}>{filiais.length} filial(is) vinculada(s)</div>
        {!associando && podeEditar && (
          <Button size="sm" color="info" disabled={!embarcadorId || saving} onClick={abrirAssociar}>
            + Associar filial
          </Button>
        )}
      </div>

      {!embarcadorId && (
        <div style={hintStyle}>Selecione um embarcador no topo para gerenciar filiais.</div>
      )}

      {associando && (
        <div style={{ background: AUTOPILOT_COLORS.surface, border: `1px solid ${AUTOPILOT_COLORS.border}`, borderRadius: "6px", padding: "0.75rem", marginBottom: "0.75rem" }}>
          <div style={{ display: "flex", gap: "0.4rem" }}>
            <Input
              bsSize="sm"
              placeholder="Buscar por fantasia ou CNPJ..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") buscarCandidatos(busca);
              }}
            />
            <Button size="sm" color="info" outline onClick={() => buscarCandidatos(busca)} disabled={loadingCand}>
              Buscar
            </Button>
            <Button size="sm" color="secondary" outline onClick={() => setAssociando(false)}>
              Fechar
            </Button>
          </div>
          <div style={{ maxHeight: "220px", overflowY: "auto", marginTop: "0.6rem", display: "flex", flexDirection: "column", gap: "0.35rem" }}>
            {loadingCand ? (
              <div style={{ textAlign: "center", padding: "0.75rem" }}><Spinner size="sm" color="info" /></div>
            ) : candidatos.length === 0 ? (
              <div style={hintStyle}>Nenhuma transportadora disponível.</div>
            ) : (
              candidatos.map((c) => (
                <div key={c.id} style={rowStyle}>
                  <div>
                    <div style={{ fontSize: "0.82rem", color: AUTOPILOT_COLORS.text }}>{c.fantasia || c.razaoSocial}</div>
                    <div style={{ fontSize: "0.72rem", color: AUTOPILOT_COLORS.muted }}>{formatDoc(c.cnpjCpf)}</div>
                  </div>
                  <Button size="sm" color="success" disabled={saving} onClick={() => associar(c.id)}>
                    Associar
                  </Button>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {!filiais.length ? (
        <div style={hintStyle}>Nenhuma filial vinculada.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          {filiais.map((f) => (
            <div key={f.id} style={rowStyle}>
              <div>
                <div style={{ fontSize: "0.85rem", color: AUTOPILOT_COLORS.text }}>{f.fantasia || f.razaoSocial}</div>
                <div style={{ fontSize: "0.75rem", color: AUTOPILOT_COLORS.muted }}>{formatDoc(f.cnpjCpf)}</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                <span style={{ fontSize: "0.72rem", padding: "2px 8px", borderRadius: "10px", background: f.status ? AUTOPILOT_COLORS.statusSuccessBg : AUTOPILOT_COLORS.insetSurface, color: f.status ? AUTOPILOT_COLORS.statusSuccess : AUTOPILOT_COLORS.statusWarning, border: `1px solid ${f.status ? AUTOPILOT_COLORS.statusSuccessBorder : AUTOPILOT_COLORS.statusWarning}` }}>
                  {f.status ? "Ativa" : "Inativa"}
                </span>
                {podeEditar && (
                  <Button size="sm" color="danger" outline disabled={saving || !embarcadorId} onClick={() => remover(f.id)}>
                    Remover
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Modal principal ──────────────────────────────────────────────────────────
const TABS = ["dados", "filiais", "integracao"];
const TAB_LABELS = { dados: "Dados Gerais", contatos: "Contatos", filiais: "Filiais", integracao: "Configurar Integração", conector: "Conector (admin)", historico: "Histórico" };

// abaInicial (PO1-3492): a Homologação abre direto em "contatos" ou "dados".
const TransportadoraDetalheModal = ({ transportadoraId, embarcadorId, onClose, abaInicial }) => {
  const [abaAtiva, setAbaAtiva] = useState(abaInicial || "dados");
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);

  const { user } = useAuth();
  const isAdmin = user?.setor === "admin";
  // PO1-2308: o admin edita os dados e vê o histórico por campo.
  // PO1-3492: e vê/edita os contatos (por embarcador) na aba Contatos.
  const tabs = isAdmin ? ["dados", "contatos", "filiais", "integracao", "conector", "historico"] : TABS;

  const carregar = useCallback(async () => {
    if (!transportadoraId) return;
    try {
      setLoading(true);
      setErro(null);
      setDados(null);
      const res = await api.get(`/transportadoras/${transportadoraId}/detalhe`, {
        params: embarcadorId ? { embarcadorId } : {},
      });
      setDados(res.data);
    } catch {
      setErro("Erro ao carregar dados da transportadora.");
    } finally {
      setLoading(false);
    }
  }, [transportadoraId, embarcadorId]);

  useEffect(() => {
    if (transportadoraId) {
      setAbaAtiva(abaInicial || "dados");
      carregar();
    }
  }, [transportadoraId, carregar]); // eslint-disable-line react-hooks/exhaustive-deps

  const navStyle = { cursor: "pointer", fontSize: "0.82rem", padding: "0.4rem 0.9rem", color: AUTOPILOT_COLORS.muted, borderRadius: "4px 4px 0 0" };
  const navActiveStyle = { ...navStyle, color: AUTOPILOT_COLORS.text, borderBottom: `2px solid ${AUTOPILOT_COLORS.accent}`, fontWeight: 600 };

  return (
    <Modal
      isOpen={!!transportadoraId}
      toggle={onClose}
      modalClassName="modal-align-top"
      fade={false}
      size="xl"
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "1rem 1.5rem 0.75rem", borderBottom: `1px solid ${AUTOPILOT_COLORS.border}` }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: "0.95rem", color: AUTOPILOT_COLORS.text }}>
            {dados ? (dados.fantasia || dados.razaoSocial) : "Transportadora"}
          </div>
          {dados && <div style={{ fontSize: "0.78rem", color: AUTOPILOT_COLORS.muted, marginTop: "2px" }}>{formatDoc(dados.cnpjCpf)}</div>}
        </div>
        <button type="button" onClick={onClose} style={{ background: "none", border: "none", color: AUTOPILOT_COLORS.muted, fontSize: "1.25rem", cursor: "pointer", lineHeight: 1 }}>×</button>
      </div>

      <div style={{ borderBottom: `1px solid ${AUTOPILOT_COLORS.border}`, padding: "0 1.5rem" }}>
        <Nav style={{ margin: 0 }}>
          {tabs.map((tab) => (
            <NavItem key={tab}>
              <NavLink style={abaAtiva === tab ? navActiveStyle : navStyle} onClick={() => setAbaAtiva(tab)}>
                {TAB_LABELS[tab]}
                {tab === "filiais" && dados?.filiais ? ` (${dados.filiais.length})` : ""}
              </NavLink>
            </NavItem>
          ))}
        </Nav>
      </div>

      <ModalBody style={{ padding: "1.25rem 1.5rem", minHeight: "300px" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: "3rem" }}><Spinner color="info" /></div>
        ) : erro ? (
          <div style={{ color: AUTOPILOT_COLORS.statusDanger, padding: "1rem" }}>{erro}</div>
        ) : (
          <TabContent activeTab={abaAtiva}>
            <TabPane tabId="dados">
              {isAdmin ? (
                <TabDadosEditavel transportadoraId={transportadoraId} onChanged={carregar} />
              ) : (
                <TabDados dados={dados} />
              )}
            </TabPane>
            {isAdmin && (
              <TabPane tabId="contatos">
                {abaAtiva === "contatos" && <ContatosTab transportadoraId={transportadoraId} />}
              </TabPane>
            )}
            <TabPane tabId="filiais">
              <TabFiliais
                filiais={dados?.filiais}
                matrizId={transportadoraId}
                embarcadorId={embarcadorId}
                onChanged={carregar}
                podeEditar={isAdmin}
              />
            </TabPane>
            <TabPane tabId="integracao">
              {abaAtiva === "integracao" && (
                <IntegracaoTab
                  transportadoraId={transportadoraId}
                  embarcadorId={embarcadorId}
                  transportadora={dados ? { nome: dados.fantasia || dados.razaoSocial, cnpj: formatDoc(dados.cnpjCpf) } : null}
                />
              )}
            </TabPane>
            {isAdmin && (
              <TabPane tabId="conector">
                {abaAtiva === "conector" && (
                  <VinculoConectorTab transportadoraId={transportadoraId} />
                )}
              </TabPane>
            )}
            {isAdmin && (
              <TabPane tabId="historico">
                {abaAtiva === "historico" && <TabHistorico transportadoraId={transportadoraId} />}
              </TabPane>
            )}
          </TabContent>
        )}
      </ModalBody>
    </Modal>
  );
};

export default TransportadoraDetalheModal;
